import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@izicvs/ui";
import { t } from "@lingui/macro";
import { motion } from "framer-motion";
import { Bot, LayoutList, Search, Shield, Star, Wand2 } from "lucide-react";
import { useState } from "react";

const FAQItem = ({
  title,
  content,
  icon: Icon,
}: {
  title: string;
  content: React.ReactNode;
  icon: React.ElementType;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <AccordionItem value={title} className="border-b border-neutral-200 dark:border-neutral-800">
      <AccordionTrigger
        className="rounded-lg py-4 transition-colors hover:bg-neutral-50 dark:hover:bg-neutral-900/50"
        onClick={() => {
          setIsOpen(!isOpen);
        }}
      >
        <div className="flex w-full items-center">
          <div
            className={`mr-4 rounded-xl p-3 transition-all duration-300 ${
              isOpen
                ? "bg-primary/10 text-primary"
                : "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400"
            } `}
          >
            <Icon size={24} />
          </div>
          <span className="grow text-left text-lg font-medium">{title}</span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4 text-neutral-600 dark:text-neutral-400">
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{
            opacity: 1,
            height: "auto",
            transition: {
              duration: 0.3,
              ease: "easeOut",
            },
          }}
          exit={{
            opacity: 0,
            height: 0,
            transition: {
              duration: 0.2,
              ease: "easeIn",
            },
          }}
        >
          {content}
        </motion.div>
      </AccordionContent>
    </AccordionItem>
  );
};

export const FAQSection = () => {
  const faqItems = [
    {
      icon: Search,
      title: t`How does Izicvs find jobs for me?`,
      content: (
        <>
          <p className="mb-4">
            {t`Izicvs pulls real openings from several sources and ranks them for your resume:`}
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>{t`Official company boards through Greenhouse, Lever and Ashby`}</li>
            <li>{t`Job aggregators such as Adzuna and Remotive`}</li>
            <li>{t`A "For You" feed that scores every job against your profile`}</li>
            <li>{t`Clear match reasons so you know why a job fits`}</li>
          </ul>
        </>
      ),
    },
    {
      icon: Wand2,
      title: t`How does the AI help me apply?`,
      content: (
        <>
          <p className="mb-4">{t`For each job, the AI does the heavy lifting:`}</p>
          <ul className="list-disc space-y-2 pl-5">
            <li>{t`An expert panel scores your fit and flags gaps`}</li>
            <li>{t`It tailors your resume and writes a cover letter, without inventing anything`}</li>
            <li>{t`It pre-fills the application form so you apply in one click`}</li>
            <li>{t`It flags scam and ghost postings before you waste time`}</li>
          </ul>
        </>
      ),
    },
    {
      icon: Bot,
      title: t`What does the autonomous agent do?`,
      content: (
        <>
          <p className="mb-4">{t`On the Autopilot plan, Izicvs keeps working while you do not:`}</p>
          <ul className="list-disc space-y-2 pl-5">
            <li>{t`It re-scans your saved searches around the clock`}</li>
            <li>{t`It prepares matching applications for you automatically`}</li>
            <li>{t`It sends a daily digest of your best new matches`}</li>
            <li>{t`You review and approve everything in one batch, nothing is sent without you`}</li>
          </ul>
        </>
      ),
    },
    {
      icon: LayoutList,
      title: t`Can I track my applications?`,
      content: (
        <>
          <p className="mb-4">{t`Yes, every application lives on a simple board:`}</p>
          <ul className="list-disc space-y-2 pl-5">
            <li>{t`A Kanban tracker from "to apply" to "offer"`}</li>
            <li>{t`Drag and drop to update a status in seconds`}</li>
            <li>{t`Applications you already sent are marked so you avoid duplicates`}</li>
            <li>{t`Each application links back to its job and analysis`}</li>
          </ul>
        </>
      ),
    },
    {
      icon: Shield,
      title: t`Is my data secure?`,
      content: (
        <>
          <p className="mb-4">{t`Protecting your data is our absolute priority:`}</p>
          <ul className="list-disc space-y-2 pl-5">
            <li>{t`Your data is never sold`}</li>
            <li>{t`AI providers process your content only to generate your results`}</li>
            <li>{t`GDPR compliance with export and deletion on request`}</li>
            <li>{t`Secure storage and guaranteed confidentiality`}</li>
          </ul>
        </>
      ),
    },
    {
      icon: Star,
      title: t`What does it cost?`,
      content: (
        <>
          <p className="mb-4">{t`Start free, upgrade only when you want more:`}</p>
          <ul className="list-disc space-y-2 pl-5">
            <li>{t`Free: resume builder, job search and tracker`}</li>
            <li>{t`Pro at 9 € per month: the full AI toolkit`}</li>
            <li>{t`Autopilot at 19 € per month: the autonomous agent`}</li>
            <li>{t`Cancel anytime, no commitment`}</li>
          </ul>
        </>
      ),
    },
  ];

  return (
    <section
      id="faq"
      className="relative bg-gradient-to-b from-transparent to-neutral-50/50 py-12 dark:to-neutral-900/30"
    >
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.6,
              ease: "easeOut",
            },
          }}
          viewport={{ once: true }}
          className="mb-16 text-center"
        >
          <h2 className="mb-6 text-4xl font-bold text-neutral-800 dark:text-neutral-200 md:text-5xl">
            {t`Frequently Asked Questions`}
          </h2>
          <p className="mx-auto max-w-4xl text-lg text-neutral-600 dark:text-neutral-400">
            {t`Everything you need to know about our resume creation service. Clear and precise answers to guide you.`}
          </p>
        </motion.div>

        <div className="mx-auto max-w-4xl">
          <Accordion collapsible type="single" className="space-y-2">
            {faqItems.map((item, index) => (
              <FAQItem key={index} title={item.title} content={item.content} icon={item.icon} />
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};
